import { CATALOGO } from "@caint/data";
import type { NivelMaturidade } from "@caint/engine";
import { PESOS_CONFORMIDADE_CAMPO, TAMANHO_AMOSTRA, corroboracao } from "@caint/relatorio";
import type { PesosConformidade } from "@caint/relatorio";
import type {
  EstratoAmostra,
  ItemCatalogo,
  LikertPonto,
  ResultadoSupervisao,
  SupAmostraItem,
  SupervisaoData,
} from "@caint/shared";

/**
 * Lib pura da Supervisão AUDIN (doc 09): geração da amostra cega (sobre itens já
 * avaliados), corroboração e indicadores do parecer (Camada 3). Sem IO.
 *
 * Os indicadores puros (conformidade de fase, diagnóstico de amostragem e
 * indicadores do parecer) e seus auxiliares foram promovidos para
 * `@caint/relatorio` (compartilhados com o backend) e são re-exportados abaixo
 * para preservar a superfície que os componentes já importam deste módulo.
 */

// --- Reexportações da lógica compartilhada (@caint/relatorio) ---
export {
  TAMANHO_AMOSTRA,
  matParaResultado,
  corroboracao,
  sugerirParecer,
  criterioOu,
  pesoCriticidade,
  PESOS_CONFORMIDADE_CAMPO,
  conformidadeFase,
  diagnosticoAmostragem,
  indicadoresParecer,
} from "@caint/relatorio";
export type {
  EscalaLikert,
  PesosConformidade,
  SubtotalResultado,
  ConformidadeFase,
  DiagnosticoAmostragem,
  IndicadoresParecer,
} from "@caint/relatorio";

/** Hash determinístico de uma string → [0,1), para o "sorteio" reprodutível. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/**
 * Limiar de risco inerente (P×I referencial) que classifica um item amostrado
 * como "RiscoInerente". Espelha `riskRM`/`gatilhoPlano` do motor (doc 11): ≥ 8 é
 * risco relevante.
 */
export const LIMIAR_RISCO_INERENTE = 8;

/** Item candidato à amostra — o mínimo necessário p/ priorizar (já avaliado). */
export interface ItemAvaliavel {
  readonly id: string;
  readonly peso: number; // 3 = CORE
  readonly p: number; // P referencial (1–5)
  readonly i: number; // I referencial (1–5)
  readonly setor: string; // 05A..05R — para restringir o pool ao escopo
  readonly mi: NivelMaturidade; // nota do avaliador (para filtrar o pool por faixa)
}

/**
 * Faixa de maturidade ELEGÍVEL para a re-performance cega: só itens avaliados
 * em 2, 3 ou 4 entram no sorteio. Itens em 0/1 (já não conformes evidentes) e NA
 * ficam de fora — a amostra foca onde a re-checagem agrega mais (doc 09 §9.5).
 */
const MATURIDADE_AMOSTRAVEL: ReadonlySet<string> = new Set(["2", "3", "4"]);
export function elegivelParaAmostra(mi: NivelMaturidade | null | undefined): boolean {
  return mi != null && MATURIDADE_AMOSTRAVEL.has(mi);
}

/**
 * Alvo efetivo da amostra (doc 17b §3.5): `min(TAMANHO_AMOSTRA, nAplicaveis)`.
 * Numa unidade pequena (poucos setores), o universo avaliável pode ser < 28 —
 * sem isso a aba 3B nunca abriria. `nAplicaveis` = nº de itens em setores ativos.
 */
export function tamanhoAmostraEfetivo(nAplicaveis: number, alvo: number = TAMANHO_AMOSTRA): number {
  return Math.min(alvo, Math.max(0, nAplicaveis));
}

/** Um item está "avaliado" quando tem nota do avaliador (mi) preenchida. */
export function notaPreenchida(mi: string | null | undefined): boolean {
  return mi != null && mi !== "";
}

/**
 * Item COMPLETO do ponto de vista do avaliador (regra de obrigatoriedade): tem
 * maturidade E — se a maturidade não for NA — evidência registrada. NA dispensa
 * evidência (item fora do cálculo). Base do gating "checklist completo" que
 * libera a amostra cega (garante aleatoriedade sobre um checklist concluído).
 */
export function itemCompleto(mi: string | null | undefined, evidencia: string | null | undefined): boolean {
  if (!notaPreenchida(mi)) return false;
  if (mi === "na") return true;
  return (evidencia ?? "").trim() !== "";
}

/**
 * Percentual mínimo do checklist do escopo (itens completos / itens aplicáveis)
 * que LIBERA a amostra cega. Não é 100% para o supervisor não esperar o último
 * item, mas alto o suficiente p/ preservar a aleatoriedade. Config nomeada
 * (doc 13 §13.8) — ajuste aqui para mudar a régua.
 */
export const LIMIAR_CHECKLIST_COMPLETO = 0.9;

/** Mínimo de itens completos p/ liberar a amostra, dado o universo aplicável. */
export function minimoParaLiberar(total: number): number {
  return Math.ceil(total * LIMIAR_CHECKLIST_COMPLETO);
}

/** A amostra está liberada quando `completos` atinge o mínimo (≥ limiar) do total. */
export function checklistLiberado(completos: number, total: number): boolean {
  return total > 0 && completos >= minimoParaLiberar(total);
}

const ehCore = (it: ItemAvaliavel): boolean => it.peso === 3;
const riscoInerente = (it: ItemAvaliavel): number => it.p * it.i;

function estratoDe(it: ItemAvaliavel): EstratoAmostra {
  if (ehCore(it)) return "CORE";
  return riscoInerente(it) >= LIMIAR_RISCO_INERENTE ? "RiscoInerente" : "Aleatorio";
}

/**
 * Amostra cega construída SOBRE os itens do checklist que JÁ têm nota do
 * avaliador (doc 09 §9.5, regra vigente) E pertencem a setores ATIVOS no escopo
 * (doc 17b §3.5). Prioriza CORE, depois maior risco inerente, completando por
 * sorteio determinístico (semente reproduzível), acumulando até `limite` itens.
 * Função pura (doc 13 §13.1): mesma entrada → mesma saída; o chamador filtra os
 * avaliados e passa a ordem do catálogo.
 *
 * `setoresAtivos` restringe o pool de forma EXPLÍCITA (defesa em profundidade —
 * não dependemos só de "não foi avaliado"); ausente ⇒ sem restrição de setor.
 */
export function gerarAmostraCega(
  itensAvaliados: readonly ItemAvaliavel[],
  opts: { limite?: number; seed?: string; setoresAtivos?: ReadonlySet<string> } = {},
): SupAmostraItem[] {
  const seed = opts.seed ?? "";
  const ativos = opts.setoresAtivos;
  // Pool: setores ativos E maturidade na faixa amostrável (2-4); 0/1/NA fora.
  const pool = itensAvaliados.filter((it) => (!ativos || ativos.has(it.setor)) && elegivelParaAmostra(it.mi));
  const limite = opts.limite ?? tamanhoAmostraEfetivo(pool.length);
  const ordem = new Map(pool.map((it, i) => [it.id, i]));
  return [...pool]
    .sort(
      (a, b) =>
        Number(ehCore(b)) - Number(ehCore(a)) || // CORE primeiro
        riscoInerente(b) - riscoInerente(a) || // depois maior risco inerente
        hash01(`${seed}:${a.id}`) - hash01(`${seed}:${b.id}`), // empate: sorteio determinístico
    )
    .slice(0, limite)
    .map((it) => ({ itemId: it.id, estrato: estratoDe(it) }))
    .sort((a, b) => (ordem.get(a.itemId) ?? 0) - (ordem.get(b.itemId) ?? 0)); // exibe na ordem do catálogo
}

/**
 * Semente do sorteio da amostra cega. `rev` 0/ausente ⇒ a semente histórica (o id
 * da avaliação), preservando os sorteios já existentes. `rev` ≥ 1 (após um
 * "refazer") deriva uma semente nova → amostra diferente na cauda aleatória.
 */
export function seedAmostra(avaliacaoId: string, rev: number | undefined): string {
  return rev && rev > 0 ? `${avaliacaoId}:r${rev}` : avaliacaoId;
}

/**
 * Reset da amostra cega para um NOVO sorteio: zera a amostra (será re-gerada com a
 * semente da próxima `amostraRev`), limpa as contestações (`resolucoes` — amostra
 * e contestação são interligadas) e incrementa `amostraRev`. Função pura.
 */
export function resetarAmostra(dados: SupervisaoData): SupervisaoData {
  return { ...dados, amostra: [], resolucoes: {}, amostraRev: (dados.amostraRev ?? 0) + 1 };
}

/**
 * Mapeia um ponto Likert → conformidade C/NC (NA é tratado fora da escala). O
 * corte é parametrizável (config única) — nenhum número mágico inline.
 */
export function conformidadeDeLikert(
  p: LikertPonto,
  pesos: PesosConformidade = PESOS_CONFORMIDADE_CAMPO,
): Extract<ResultadoSupervisao, "C" | "NC"> {
  return p >= pesos.likert.corteConforme ? "C" : "NC";
}

/**
 * Equivalência reversa p/ EXIBIR respostas legadas (só C/NC/NA) no seletor
 * Likert: C → ponto máximo, NC → ponto mínimo, NA/vazio → sem ponto. Apenas
 * para apresentação — a fonte de verdade nova é a própria Likert.
 */
export function likertDeConformidade(
  r: ResultadoSupervisao | null | undefined,
  pesos: PesosConformidade = PESOS_CONFORMIDADE_CAMPO,
): LikertPonto | null {
  const valores = pesos.likert.pontos.map((p) => p.valor);
  if (r === "C") {
    return Math.max(...valores) as LikertPonto; // ponto máximo (ex.: 5)
  }
  if (r === "NC") {
    return Math.min(...valores) as LikertPonto; // ponto mínimo (ex.: 1)
  }
  return null; // NA ou vazio → fora da escala
}

/** Pergunta/legenda do item de catálogo (para mostrar na amostra). */
export function itemDoCatalogo(itemId: string): ItemCatalogo | undefined {
  return CATALOGO.find((it) => it.id === itemId);
}

/** Uma divergência amostra × checklist a ser arbitrada pelo supervisor. */
export interface Divergencia {
  readonly itemId: string;
  readonly notaAval: NivelMaturidade; // nota do avaliador (checklist)
  readonly matSup: NivelMaturidade; // maturidade cega do supervisor (amostra)
}

/**
 * Divergências da amostra: itens em que a maturidade cega do supervisor diverge
 * (≥2 níveis, ou um lado NA) da nota do avaliador. Só itens com ambos os valores
 * — são exatamente os que o supervisor precisa arbitrar (qual nota prevalece).
 */
export function divergenciasAmostra(
  dados: SupervisaoData,
  notaPorItem: Map<string, string | null>,
): Divergencia[] {
  const out: Divergencia[] = [];
  for (const a of dados.amostra) {
    const nota = notaPorItem.get(a.itemId) ?? null;
    if (!a.matSup || !nota) {
      continue;
    }
    if (corroboracao(a.matSup, nota) === "Diverge") {
      out.push({ itemId: a.itemId, notaAval: nota as NivelMaturidade, matSup: a.matSup });
    }
  }
  return out;
}

/**
 * Overrides de maturidade decididos pelo supervisor: para cada item divergente
 * resolvido como "supervisor", o IMGQ passa a usar a maturidade do supervisor
 * (matSup) no lugar da nota do avaliador. "avaliador" (ou sem decisão) = sem
 * override. Aplica-se ao cálculo dos índices da avaliação supervisionada.
 */
export function overridesSupervisor(dados: SupervisaoData): Record<string, NivelMaturidade> {
  const resol = dados.resolucoes ?? {};
  const matSupPorItem = new Map(dados.amostra.map((a) => [a.itemId, a.matSup]));
  const over: Record<string, NivelMaturidade> = {};
  for (const [itemId, r] of Object.entries(resol)) {
    const matSup = matSupPorItem.get(itemId);
    if (r.escolha === "supervisor" && matSup) {
      over[itemId] = matSup;
    }
  }
  return over;
}
